import { apiFetch, ApiNetworkError } from './apiClient';
import { defaultErrorMessage } from './httpRequest';

/**
 * #862 -- the signed-in user's preferences: settings that follow them across devices. A free-form
 * JSON object whose keys the client owns (`styleDefaults` first, #842 S9). Revisioned like
 * drawings: a save carries the revision that was read, and a stale one gets a conflict.
 */
export interface StoredPreferences {
  readonly revision: number;
  readonly preferences: Readonly<Record<string, unknown>>;
}

export class PreferencesApiError extends Error {
  readonly status: number;
  /** Present on a 409: the revision another device saved. */
  readonly currentRevision: number | undefined;

  constructor(message: string, status: number, currentRevision?: number) {
    super(message);
    this.name = 'PreferencesApiError';
    this.status = status;
    this.currentRevision = currentRevision;
  }
}

async function request(init?: RequestInit): Promise<StoredPreferences> {
  let response: Response;
  try {
    response = await apiFetch('/me/preferences', init);
  } catch (error) {
    throw new PreferencesApiError(
      error instanceof ApiNetworkError
        ? `Unable to reach the preferences API: ${error.message}`
        : 'Unable to reach the preferences API.',
      0
    );
  }
  if (response.status === 401) throw new PreferencesApiError('Session expired. Redirecting to sign in…', 401);
  if (response.status === 409) {
    const problem = (await response.json().catch(() => undefined)) as { currentRevision?: unknown } | undefined;
    throw new PreferencesApiError(
      'Your preferences were changed on another device.',
      409,
      typeof problem?.currentRevision === 'number' ? problem.currentRevision : undefined
    );
  }
  if (!response.ok) throw new PreferencesApiError(await defaultErrorMessage(response, 'preferences'), response.status);
  return (await response.json()) as StoredPreferences;
}

export function getPreferences(): Promise<StoredPreferences> {
  return request();
}

export function savePreferences(
  revision: number,
  preferences: Readonly<Record<string, unknown>>
): Promise<StoredPreferences> {
  return request({
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ revision, preferences })
  });
}
